import { useTranslation } from "react-i18next";
import {
  PlayerWithScorecard,
  Category,
  ScoreVisibility,
  SCHOOL_CATEGORIES,
  COMBO_CATEGORIES,
  SchoolCategory,
  ComboCategory,
  filledCount,
} from "../../types/game";
import Avatar from "../common/Avatar";

import { AnimatedTotal } from "../common/AnimatedTotal";


interface Props {
  players: PlayerWithScorecard[];
  myPlayerId: string | null;
  isAdmin: boolean;
  gameFinished: boolean;
  scoreVisibility: ScoreVisibility;
  isStrict?: boolean;
  currentTurnPlayerId?: string | null;
  rollCount?: number;
  cardClass?: string;
  accentClass?: string;
  compact?: boolean;
  onCellClick: (playerId: string, category: Category) => void;
}

function schoolDisplay(val: number | null): { text: string; color: string; bg: string } {
  if (val === null) return { text: "", color: "", bg: "glass-cell" };
  if (val === 0) return { text: "✕", color: "text-yellow-300", bg: "bg-yellow-500/20 border border-yellow-500/30" };
  if (val > 0) return { text: `+${val}`, color: "text-green-300", bg: "bg-green-500/20 border border-green-500/30" };
  return { text: `${val}`, color: "text-red-300", bg: "bg-red-500/20 border border-red-500/30" };
}

function comboDisplay(val: number | null): { text: string; color: string; bg: string } {
  if (val === null) return { text: "", color: "", bg: "glass-cell" };
  if (val === 0) return { text: "−", color: "text-red-400 text-xl", bg: "bg-red-900/40 border border-red-800/50" };
  return { text: String(val), color: "text-blue-100 font-bold", bg: "bg-blue-500/20 border border-blue-500/30 shadow-[0_0_10px_rgba(59,130,246,0.2)]" };
}

export default function Scorecard({
  players,
  myPlayerId,
  isAdmin,
  gameFinished,
  scoreVisibility,
  isStrict = false,
  currentTurnPlayerId = null,
  rollCount = 0,
  cardClass = "glass",
  accentClass = "text-blue-400",
  compact = false,
  onCellClick,
}: Props) {
  const { t } = useTranslation();

  // Density classes — compact mode squeezes rows so the whole table fits on
  // tall screens (foldables, laptops) without scrolling
  const cellCls = compact ? "w-10 h-7 rounded-lg text-xs" : "w-12 h-10 rounded-xl text-sm";
  const cellPad = compact ? "py-[3px] px-1" : "py-2 px-2";
  const labelPad = compact ? "py-[3px] px-2" : "py-2 px-2";
  const subPad = compact ? "py-1.5 px-2" : "py-3 px-2";
  const secPad = compact ? "pt-1.5 pb-0.5 px-2" : "pt-3 pb-1 px-2";

  const canEdit = (playerId: string) => {
    if (gameFinished) return false;
    
    if (isStrict) {
      // In strict mode, only current player can edit, and only after rolling
      return playerId === myPlayerId && playerId === currentTurnPlayerId && rollCount > 0;
    }

    // Free mode: admin and any player in the game can edit the whole table
    if (isAdmin) return true;
    return myPlayerId !== null;
  };

  // Hide totals rows if admin chose "final only" and game isn't done yet.
  // Applies to everyone, admin included — nobody should see sums mid-game.
  // Individual cells always stay visible: the table is shared and any
  // participant can verify or edit any entry.
  const hideTotals = scoreVisibility === "final_only" && !gameFinished;

  // Find active player (player with minimum filled turns).
  // filledCount works on redacted final_only payloads too.
  const turnCounts = players.map((p) => filledCount(p));
  const minTurns = Math.min(...turnCounts);
  const activePlayerIndex = turnCounts.findIndex((c) => c === minTurns);
  const activePlayerId = players[activePlayerIndex]?.player.id;

  return (
    <div className={`overflow-x-auto rounded-3xl border-2 ${cardClass} shadow-2xl transition-all duration-700`}>
      <table className="w-full text-sm border-collapse min-w-[300px]">
        {/* Player headers */}
        <thead>
          <tr>
            <th className={`text-left ${compact ? "py-1" : "py-2"} px-2 text-xs text-ink/30 font-normal w-28 sticky left-0 glass-sticky z-10`}>
              {t("game.category")}
            </th>
            {players.map(({ player }) => (
              <th
                key={player.id}
                className={`${compact ? "py-1 px-1 w-14" : "py-2 px-2 w-16"} text-center ${player.id === myPlayerId ? accentClass : "text-ink/70"}`}
              >
                <div className="flex justify-center">
                  <Avatar src={player.avatar} size="sm" />
                </div>
                <div className={`${compact ? "text-[10px] mt-0" : "text-xs mt-0.5"} truncate max-w-14 mx-auto`}>
                  {player.display_name}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* ── School ─────────────────────────────────────────── */}
          <tr>
            <td colSpan={players.length + 1} className={secPad}>
              <span className="text-[10px] font-black text-ink/30 uppercase tracking-[0.22em]">
                {t("game.school")}
              </span>
            </td>
          </tr>
          {SCHOOL_CATEGORIES.map((cat) => (
            <tr key={cat} className="border-t border-ink/[0.06]">
              <td className={`${labelPad} text-ink/45 sticky left-0 glass-sticky z-10 text-xs`}>
                {t(`categories.${cat}`)}
              </td>
              {players.map((entry) => {
                const { player, scorecard } = entry;
                const val = scorecard[cat as SchoolCategory];
                const { text, color, bg } = schoolDisplay(val);
                const editable = canEdit(player.id);
                return (
                  <td key={player.id} className={`${cellPad} text-center`}>
                    <button
                      onClick={() =>
                        editable &&
                        onCellClick(player.id, cat)
                      }
                      disabled={!editable}
                      className={`${cellCls} font-semibold transition-all duration-200 ${color} ${bg} ${
                        editable
                          ? "hover:scale-110 active:scale-95 cursor-pointer shadow-sm hover:shadow-md hover:bg-ink/10"
                          : "cursor-default"
                      } ${player.id === activePlayerId && val === null ? "ring-2 ring-blue-400/80 shadow-[0_0_8px_rgba(96,165,250,0.5)] animate-pulse" : ""}`}
                    >
                      {text}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}

          {/* School subtotal */}
          <tr className="glass-row-sub">
            <td className={`${subPad} text-[10px] font-black text-ink/40 sticky left-0 glass-sticky z-10 uppercase tracking-widest`}>
              {t("game.school")} ∑
            </td>
            {players.map(({ player, totals }) => {
              if (hideTotals)
                return (
                  <td
                    key={player.id}
                    className={`${subPad} text-center text-ink/25 text-xs`}
                  >
                    ?
                  </td>
                );
              
              const isClosed = totals.school_complete;
              const color =
                totals.school_total > 0
                  ? "text-green-400"
                  : totals.school_total < 0
                    ? "text-red-400"
                    : "text-ink/60";
              
              const bg = isClosed
                ? (totals.school_total > 0 ? "bg-green-900/30 shadow-inner" : totals.school_total < 0 ? "bg-red-900/30 shadow-inner" : "glass-cell shadow-inner")
                : "";

              return (
                <td
                  key={player.id}
                  className={`${subPad} text-center text-sm font-bold ${color} ${bg} transition-all`}
                >
                  <div className={isClosed ? "text-base font-black scale-110" : ""}>
                    <AnimatedTotal
                      value={totals.school_total}
                      prefix={totals.school_total > 0 ? "+" : ""}
                    />
                  </div>
                  {isClosed && totals.school_bonus !== 0 && (
                    <div className={`text-[10px] mt-0.5 ${totals.school_bonus > 0 ? "text-green-300/80" : "text-red-300/80"}`}>
                      ({totals.school_bonus > 0 ? `+${totals.school_bonus}` : totals.school_bonus} {t("game.bonus")})
                    </div>
                  )}
                </td>
              );
            })}
          </tr>

          {/* ── Combos ─────────────────────────────────────────── */}
          <tr>
            <td colSpan={players.length + 1} className={compact ? secPad : "pt-4 pb-1 px-2"}>
              <span className="text-[10px] font-black text-ink/30 uppercase tracking-[0.22em]">
                {t("game.combinations")}
              </span>
            </td>
          </tr>
          {COMBO_CATEGORIES.map((cat) => {
            const isGarbage = cat === "garbage";
            return (
            <tr key={cat} className={`border-t ${isGarbage ? "border-[3px] border-ink/[0.14]" : "border-ink/[0.06]"}`}>
              <td className={`${labelPad} text-ink/45 sticky left-0 glass-sticky z-10 text-xs ${isGarbage ? "font-semibold italic" : ""}`}>
                {t(`categories.${cat}`)}
              </td>
              {players.map((entry) => {
                const { player, scorecard } = entry;
                const val = scorecard[cat as ComboCategory];
                const { text, color, bg } = comboDisplay(val);

                // Restrict combos in first 3 rounds
                const isLocked = filledCount(entry) < 3 && val === null;
                const editable = canEdit(player.id) && !isLocked;
                // Lucky first-throw score ("from hand", ×2) — gold glowing frame
                const isFromHand =
                  val !== null && val !== 0 && (scorecard.from_hand?.includes(cat) ?? false);

                return (
                  <td key={player.id} className={`${cellPad} text-center`}>
                    <button
                      onClick={() =>
                        editable &&
                        onCellClick(player.id, cat)
                      }
                      disabled={!editable}
                      className={`${cellCls} font-semibold transition-all duration-200 ${color} ${isLocked ? 'glass-cell opacity-40 cursor-not-allowed' : bg} ${
                        editable
                          ? "hover:scale-110 active:scale-95 cursor-pointer shadow-sm hover:shadow-md hover:bg-ink/10"
                          : ""
                      } ${player.id === activePlayerId && val === null && !isLocked ? "ring-2 ring-blue-400/80 shadow-[0_0_8px_rgba(96,165,250,0.5)] animate-pulse" : ""} ${
                        isFromHand ? "from-hand-glow" : ""
                      }`}
                    >
                      {isLocked ? "🔒" : text}
                    </button>
                  </td>
                );
              })}
            </tr>
          )})}

          {/* Combo subtotal */}
          <tr className="glass-row-sub">
            <td className={`${subPad} text-[10px] font-black text-ink/40 sticky left-0 glass-sticky z-10 uppercase tracking-widest`}>
              {t("game.combinations")} ∑
            </td>
            {players.map(({ player, totals }) => {
              if (hideTotals)
                return (
                  <td
                    key={player.id}
                    className={`${subPad} text-center text-ink/25 text-xs`}
                  >
                    —
                  </td>
                );

              const isClosed = totals.combo_complete;
              const bg = isClosed ? "bg-blue-900/30 shadow-inner" : "";

              return (
                <td
                  key={player.id}
                  className={`${subPad} text-center text-sm font-bold text-blue-100 ${bg} transition-all`}
                >
                  <div className={isClosed ? "text-base font-black scale-110" : ""}>
                    <AnimatedTotal value={totals.combo_total} />
                  </div>
                  {isClosed && totals.combo_completion_bonus !== 0 && (
                    <div className="text-[10px] text-blue-300/80 mt-0.5">
                      +{totals.combo_completion_bonus} {t("game.bonus")}
                    </div>
                  )}
                </td>
              );
            })}
          </tr>

          {/* Grand total */}
          <tr className="glass-row-total">
            <td className={`${subPad} font-bold text-ink sticky left-0 glass-sticky z-10`}>
              {t("game.total")}
            </td>
            {players.map(({ player, totals }) => (
              <td
                key={player.id}
                className={`${subPad} text-center font-bold ${compact ? "text-base" : "text-lg"} text-ink`}
              >
                {hideTotals ? "?" : (
                  <AnimatedTotal
                    value={totals.grand_total}
                    className="tabular-nums"
                  />
                )}
              </td>
            ))}
          </tr>
        </tbody>
      </table>

    </div>
  );
}
